'use client';

import { Guess, PuzzleResultsState } from '@wt/app/game/_components/types';
import { RoundResultsSummary, GameImageElement } from '@wt/app/game/_elements';
import { MobileRoundStepper } from '@wt/game/components/elements/round-progress/MobileRoundStepper';
import { useRoundProgress } from '@wt/game/providers/roundProgress/RoundProgressProvider';
import { SettingsStateProps } from '@wt/game/providers/settings/types';
import { useEffect, useState } from 'react';
import {
  getGameScore,
  getMaxScore,
  MAX_DISTANCE_SCORE,
  MAX_YEAR_SCORE,
} from '../../_utils/getGameScore';
import { GameModeTag } from '../GameModeTag';
import {
  EMobileGameView,
  ImageButton,
  MapButton,
} from '../roundGame/RoundGameMobile';
import {
  initialiseRoundResultAnimations,
  MapDistanceMemo,
  MediaLisence,
  RoundResultsFooterNav,
} from './RoundResults';
import { getSiteMode, hasHints } from '@wt/utilities/siteMode';
import { getScoreIcon } from '../RoundSummary';
import BotInfo from './BotInfo';
import { UsedHintPanel } from './RoundResultsDesktop';

const RoundNumber = ({
  roundNumber,
  stepperExpanded,
  setStepperExpaned,
}: {
  roundNumber: number;
  stepperExpanded: boolean;
  setStepperExpaned: (e: boolean) => void;
}) => {
  return (
    <>
      {!stepperExpanded && (
        <div
          onClick={() => setStepperExpaned(true)}
          className="absolute left-4 top-4 z-20 flex size-9 shrink-0 items-center justify-center rounded-full border-4 border-double border-white bg-highlight dark:bg-highlight-dark font-semibold text-primary-content shadow-lg"
        >
          {roundNumber}
        </div>
      )}
      {stepperExpanded && (
        <div className="absolute left-4 top-4 z-20">
          <MobileRoundStepper closeStepper={() => setStepperExpaned(false)} />
        </div>
      )}
    </>
  );
};

export const MobileRoundScores = (props: {
  guess: Guess;
  unit: string;
  botGuess?: boolean;
}) => {
  const roundScores = getGameScore(props.guess, props.unit);
  const yearScore = roundScores.score.year;
  const distanceScore = roundScores.score.distance;
  const totalScore = roundScores.score.total;

  const scoreIcon = getScoreIcon(totalScore);

  // styling
  const backgroundColor = props.botGuess ? 'bg-base-100' : 'bg-base-200';
  const justify = props.botGuess ? 'justify-center' : 'justify-between';
  const padding = props.botGuess ? 'px-2 py-1.5' : 'px-4 py-2';
  const scoreTextSize = props.botGuess ? 'text-xs' : 'text-sm';
  const maxScoreTextSize = props.botGuess ? 'text-[8px]' : 'text-[10px]';
  const maxW = props.botGuess ? '' : 'max-w-[400px]';

  return (
    <div
      className={`flex w-full ${maxW} flex-row items-center ${justify} gap-3 wheretaken:justify-center`}
    >
      {getSiteMode() != 'wheretaken' && (
        <>
          <div
            className="flex flex-col items-center justify-center gap-1"
            data-animate-element={
              props.botGuess ? undefined : 'year-score-pill'
            }
          >
            <div className={`rounded-full ${backgroundColor} ${padding}`}>
              <div className="flex items-end">
                <span
                  className={`${scoreTextSize} font-semibold movies:dark:text-primary`}
                >
                  🗓️ {yearScore}
                </span>
                <span
                  className={`${maxScoreTextSize} font-semibold dark:text-info-content`}
                >
                  /{MAX_YEAR_SCORE}
                </span>
              </div>
            </div>
          </div>

          <div
            className="flex flex-col items-center justify-center gap-1"
            data-animate-element={
              props.botGuess ? undefined : 'location-score-pill'
            }
          >
            <div className={`rounded-full ${backgroundColor} ${padding}`}>
              <div className="flex items-end">
                <span
                  className={`${scoreTextSize} font-semibold movies:dark:text-primary`}
                >
                  📍 {distanceScore}
                </span>
                <span
                  className={`${maxScoreTextSize} font-semibold dark:text-info-content`}
                >
                  /{MAX_DISTANCE_SCORE}
                </span>
              </div>
            </div>
          </div>
        </>
      )}

      <div
        className="flex flex-col items-center justify-center gap-1"
        data-animate-element={props.botGuess ? undefined : 'total-score-pill'}
        data-score={totalScore}
      >
        <div className={`rounded-full ${backgroundColor} ${padding}`}>
          <div className="flex items-end">
            <span
              className={`${scoreTextSize} font-semibold movies:dark:text-primary`}
            >
              {scoreIcon} {totalScore}
            </span>
            <span
              className={`${maxScoreTextSize} font-semibold dark:text-info-content`}
            >
              /{getMaxScore()}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export const RoundResultsMobile = ({
  results,
  settings,
  lastGuess,
  roundNumber,
}: {
  settings?: SettingsStateProps;
  lastGuess?: Guess;
  results: PuzzleResultsState;
  roundNumber: number;
}) => {
  const { gameMode, puzzleId, currentRound } = useRoundProgress();

  const [view, setView] = useState<EMobileGameView>(EMobileGameView.map);
  const [stepperExpanded, setStepperExpanded] = useState<boolean>(false);

  //blured image for game 2025-06-11 round 3
  const isDismissed =
    typeof window !== 'undefined' &&
    localStorage.getItem('proceedModalDismissed') === 'true';

  const shouldBlurImage =
    getSiteMode() === 'whentaken' &&
    !isDismissed &&
    results?.date === '2025-06-11' &&
    results?.round === 3;

  const [bluredImage, setBluredImage] = useState(shouldBlurImage);

  useEffect(() => {
    setBluredImage(shouldBlurImage);
  }, [shouldBlurImage]);

  if (!lastGuess || !settings) return <></>;

  const isLastRound =
    results.guesses && results.guesses.length === results.puzzle.length;

  return (
    <div
      className="size-full"
      data-puzzle-id={puzzleId}
      data-current-round={currentRound}
      data-should-animate={!settings?.disableRoundEndAnimation}
      ref={initialiseRoundResultAnimations}
    >
      <div className="flex size-full flex-col items-center justify-start gap-4 px-2 py-1">
        {gameMode != 'daily' && (
          <div className="mb-3 flex w-full items-center justify-center">
            <GameModeTag />
          </div>
        )}
        <div className="flex w-full shrink flex-col gap-1">
          <div className="relative aspect-[6/5] max-h-96 w-full">
            <div
              className={`absolute size-full overflow-hidden rounded-md bg-base-100 transition-all duration-500 ease-in-out ${view === EMobileGameView.image ? 'z-10 scale-100' : 'translate-x-full translate-y-full scale-0'}`}
            >
              <GameImageElement
                results={results}
                puzzle={lastGuess.puzzle}
                mode="results"
                blur={bluredImage}
              />
            </div>
            <div
              className={`absolute size-full rounded-md transition-all duration-500 ease-in-out ${view === EMobileGameView.map ? 'z-10 scale-100' : 'translate-x-full translate-y-full scale-0'}`}
            >
              <MapDistanceMemo
                guess={lastGuess}
                puzzle={lastGuess.puzzle}
                showHintButton={hasHints()}
              />
            </div>

            {view === EMobileGameView.image && (
              <MapButton
                hasLocation={true}
                isResults={true}
                onClick={() => setView(EMobileGameView.map)}
              />
            )}

            {view === EMobileGameView.map && (
              <ImageButton
                src={lastGuess.puzzle.url}
                onClick={() => setView(EMobileGameView.image)}
                blur={bluredImage}
              />
            )}

            <RoundNumber
              roundNumber={roundNumber}
              stepperExpanded={stepperExpanded}
              setStepperExpaned={setStepperExpanded}
            />
          </div>

          {view === EMobileGameView.image && (
            <MediaLisence
              info_url={lastGuess.puzzle.info_url}
              photographer={lastGuess.puzzle.photographer}
              lisence={lastGuess.puzzle.license}
              userSubmitted={lastGuess.puzzle.userSubmitted ?? false}
            />
          )}
        </div>

        <MobileRoundScores guess={lastGuess} unit={settings.distanceUnit} />

        <div className="my-3">
          <RoundResultsSummary
            lastGuess={lastGuess}
            unit={settings.distanceUnit}
          />
        </div>

        {lastGuess?.hintsUsed && (
          <UsedHintPanel guessNumber={results?.guesses?.length ?? 0} />
        )}

        <div
          data-animate-element="scroll-to-bottom"
          className="scroll-mb-[120px] -mt-4 w-full"
        ></div>
        {getSiteMode() == 'whentaken' && <BotInfo round={lastGuess.puzzle} />}

        <div className="min-h-[70px] shrink" />
      </div>
      <div className="fixed bottom-0 left-0 z-[90] w-full">
        <div className="w-full bg-gradient-to-t from-base-100 h-8"></div>
        <div className="w-full bg-base-100 p-3">
          <RoundResultsFooterNav lastRound={isLastRound} />
        </div>
      </div>
    </div>
  );
};
